import React, { useState, useEffect, useCallback } from 'react';
import { calendarAPI, incomeAPI, financeAPI } from '../lib/api';
import { formatINR, formatINRShort, formatPercent, todayISO, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import DatePicker from '../components/DatePicker';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  Plus,
  Loader2,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  ShieldCheck,
  AlertTriangle,
  Gauge,
  Banknote,
  Info,
  Activity
} from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EMPTY_INCOME = () => ({ client_name: '', amount: '', date: '', payment_type: 'Non-GST', description: '' });

export default function CalendarView() {
  const { can } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingIncome, setSavingIncome] = useState(false);

  const [incomeForm, setIncomeForm] = useState(EMPTY_INCOME());

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const year = String(currentDate.getFullYear());
      
      const [summaryRes, eventsRes] = await Promise.all([
        calendarAPI.getDailySummary(month, year),
        calendarAPI.getEvents(month, year)
      ]);
      
      setCalendarData(summaryRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch calendar data'));
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const getDayData = (day) => {
    if (!calendarData?.daily_data) return null;
    return calendarData.daily_data.find(d => d.day === day);
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);

  const handleAddIncome = async (e) => {
    e.preventDefault();
    if (savingIncome) return; // a double click would record the income twice
    setSavingIncome(true);
    try {
      await incomeAPI.create({
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
        client_id: 'manual'
      });
      toast.success('Income entry added');
      setDialogOpen(false);
      setIncomeForm(EMPTY_INCOME());
      fetchCalendarData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to add income entry'));
    } finally {
      setSavingIncome(false);
    }
  };

  // Generate calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the first day of month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
  }
  
  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dayData = getDayData(day);
    const isToday = day === new Date().getDate() && 
                    currentDate.getMonth() === new Date().getMonth() && 
                    currentDate.getFullYear() === new Date().getFullYear();
    const hasIncome = dayData && dayData.income > 0;
    const hasExpenses = dayData && dayData.expenses > 0;
    
    calendarDays.push(
      <div 
        key={day}
        className={`h-24 border border-gray-100 p-1 cursor-pointer transition-colors hover:bg-gray-50 ${
          isToday ? 'bg-black/5 ring-2 ring-black' : ''
        } ${selectedDay === day ? 'bg-gray-100' : ''}`}
        onClick={() => setSelectedDay(day)}
        data-testid={`calendar-day-${day}`}
      >
        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-black font-bold' : 'text-gray-700'}`}>
          {day}
        </div>
        {dayData && (
          <div className="space-y-0.5">
            {hasIncome && (
              <div className="flex items-center gap-0.5 text-xs text-green-700 bg-green-50 px-1 py-0.5 rounded truncate">
                <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                <span className="font-mono truncate" title={formatINR(dayData.income)}>
                  <span className="hidden xl:inline">{dayData.income >= 100000 ? formatINRShort(dayData.income) : formatINR(dayData.income)}</span>
                  <span className="xl:hidden">{formatINRShort(dayData.income)}</span>
                </span>
              </div>
            )}
            {hasExpenses && (
              <div className="flex items-center gap-0.5 text-xs text-red-700 bg-red-50 px-1 py-0.5 rounded truncate">
                <ArrowDownRight className="w-3 h-3 flex-shrink-0" />
                <span className="font-mono truncate" title={formatINR(dayData.expenses)}>
                  <span className="hidden xl:inline">{dayData.expenses >= 100000 ? formatINRShort(dayData.expenses) : formatINR(dayData.expenses)}</span>
                  <span className="xl:hidden">{formatINRShort(dayData.expenses)}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const selectedDayData = selectedDay ? getDayData(selectedDay) : null;

  if (loading && !calendarData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Calendar</h1>
          <p className="text-gray-500 mt-1">Daily income and expense tracking</p>
        </div>
        {can('write') && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-income-btn" className="bg-black hover:bg-black/90">
              <Plus className="w-4 h-4 mr-2" />
              Record Income
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Income Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddIncome} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="clientName">Client Name</Label>
                <Input
                  id="clientName"
                  value={incomeForm.client_name}
                  onChange={(e) => setIncomeForm({ ...incomeForm, client_name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="incomeAmount">Amount (₹)</Label>
                  <Input
                    id="incomeAmount"
                    type="number"
                    value={incomeForm.amount}
                    onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="incomeDate">Date</Label>
                  <DatePicker
                    id="incomeDate"
                    value={incomeForm.date}
                    onChange={(v) => setIncomeForm({ ...incomeForm, date: v })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentType">Payment Type</Label>
                <Select value={incomeForm.payment_type} onValueChange={(v) => setIncomeForm({ ...incomeForm, payment_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GST">GST</SelectItem>
                    <SelectItem value="Non-GST">Non-GST</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={savingIncome} className="bg-black hover:bg-black/90">
                  {savingIncome ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Entry'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <Tabs defaultValue="cashflow">
        <TabsList>
          <TabsTrigger value="cashflow">Cashflow Calendar</TabsTrigger>
          <TabsTrigger value="reserve">Cash Reserve</TabsTrigger>
        </TabsList>

        <TabsContent value="cashflow" className="mt-6 space-y-8">

      {/* Monthly Summary */}
      {calendarData?.monthly_totals && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Monthly Income</p>
                  <p className="text-lg font-bold font-mono text-green-700">
                    {formatINR(calendarData.monthly_totals.total_income)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-red-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Monthly Expenses</p>
                  <p className="text-lg font-bold font-mono text-red-700">
                    {formatINR(calendarData.monthly_totals.total_expenses)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${calendarData.monthly_totals.net >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <DollarSign className={`w-5 h-5 ${calendarData.monthly_totals.net >= 0 ? 'text-green-700' : 'text-red-700'}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Net Cash Flow</p>
                  <p className={`text-lg font-bold font-mono ${calendarData.monthly_totals.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatINR(calendarData.monthly_totals.net)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Avg. Daily Income</p>
                <p className="text-lg font-bold font-mono">
                  {formatINR(calendarData.monthly_totals.avg_daily_income)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Avg. Daily Expense</p>
                <p className="text-lg font-bold font-mono">
                  {formatINR(calendarData.monthly_totals.avg_daily_expenses)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={goToPreviousMonth} aria-label="Previous month">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <CardTitle className="text-lg">
                  {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={goToNextMonth} aria-label="Next month">
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-0 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
                {calendarDays}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Day Details Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedDay ? `Day ${selectedDay}` : 'Select a Day'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDayData ? (
                <div className="space-y-4">
                  {/* Income items */}
                  {selectedDayData.income_items?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Income</p>
                      <div className="space-y-2">
                        {selectedDayData.income_items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-green-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-gray-500">{item.type}</p>
                            </div>
                            <p className="font-mono text-sm text-green-700">{formatINR(item.amount)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expense items */}
                  {selectedDayData.expense_items?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Expenses</p>
                      <div className="space-y-2">
                        {selectedDayData.expense_items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-gray-500">{item.category}</p>
                            </div>
                            <p className="font-mono text-sm text-red-700">{formatINR(item.amount)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Income</span>
                      <span className="font-mono text-green-700">{formatINR(selectedDayData.income)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Expenses</span>
                      <span className="font-mono text-red-700">{formatINR(selectedDayData.expenses)}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Net</span>
                      <span className={`font-mono ${selectedDayData.income - selectedDayData.expenses >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatINR(selectedDayData.income - selectedDayData.expenses)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Click on a day to see details</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          {events.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Upcoming Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {events.slice(0, 10).map((event, idx) => (
                    <div 
                      key={idx} 
                      className={`flex justify-between items-center gap-3 p-2 rounded-lg ${
                        event.type === 'income' ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate" title={event.title}>{event.title}</p>
                        <p className="text-xs text-gray-500">Day {event.day}</p>
                      </div>
                      <p className={`font-mono text-xs shrink-0 whitespace-nowrap ${event.type === 'income' ? 'text-green-700' : 'text-red-700'}`}>
                        {formatINR(event.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

        </TabsContent>

        <TabsContent value="reserve" className="mt-6">
          <CashReserveTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================ CASH RESERVE ============================ */

const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const inrL = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 100000) return '₹' + (v / 100000).toFixed(2) + 'L';
  return inr(v);
};
const RES_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function CashReserveTab() {
  const [viewDate, setViewDate] = useState(new Date());
  const [selected, setSelected] = useState(todayISO());
  const [safetyMonths, setSafetyMonths] = useState('2');
  const [cashOverride, setCashOverride] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFormulas, setShowFormulas] = useState(false);
  const [includeShared, setIncludeShared] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selected, safety_months: Number(safetyMonths), include_shared_costs: includeShared };
      if (cashOverride !== '') params.cash_override = Number(cashOverride);
      const res = await financeAPI.cashReserve(params);
      setData(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to load cash reserve'));
    } finally {
      setLoading(false);
    }
  }, [selected, safetyMonths, cashOverride, includeShared]);
  useEffect(() => { load(); }, [load]);

  const y = viewDate.getFullYear(), m = viewDate.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isSel = iso === selected;
    const isToday = iso === todayISO();
    cells.push(
      <button key={d} onClick={() => setSelected(iso)}
        className={`h-10 rounded-md text-sm transition-colors ${isSel ? 'bg-black text-white' : 'hover:bg-gray-100'} ${isToday && !isSel ? 'ring-1 ring-black' : ''}`}>
        {d}
      </button>
    );
  }

  const r = data?.reserve, c = data?.cash, b = data?.burn, h = data?.health, cy = data?.cycle, nt = data?.near_term;
  const short = r && r.surplus_or_shortfall < 0;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-gradient-to-br from-gray-50 to-white p-4 text-sm text-gray-600 flex gap-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>How much cash DESENO must keep in the bank to keep running — on the date you pick. Built on the SLC Basic Finance method: <b>Reserve Cash = Inflow − Outflow</b>, the Operating Cash Cycle, and a safety buffer. Tune the assumptions; click any date.</span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Label className="text-xs text-gray-500">Date</Label>
          <DatePicker value={selected} onChange={setSelected} />
        </div>
        <div className="w-40">
          <Label className="text-xs text-gray-500">Safety reserve</Label>
          <Select value={safetyMonths} onValueChange={setSafetyMonths}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 month</SelectItem>
              <SelectItem value="2">2 months</SelectItem>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs text-gray-500">Cash on hand override (₹)</Label>
          <Input type="number" placeholder={c ? Math.round(c.net_cash) : 'reconciled balance'}
            value={cashOverride} onChange={(e) => setCashOverride(e.target.value)} />
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 bg-gray-50 rounded-md px-3 py-2 max-w-2xl">
        <div>
          <Label htmlFor="include-shared" className="text-sm font-medium">Include team salaries &amp; shared office costs</Label>
          <p className="text-xs text-gray-500">Salaries and some office costs are currently paid from both companies&apos; accounts. Off = only costs tagged to DESENO.</p>
        </div>
        <Switch id="include-shared" checked={includeShared} onCheckedChange={setIncludeShared} />
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card><CardContent className="p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Cash on hand</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${c.net_cash < 0 ? 'text-red-700' : ''}`}>{inr(c.net_cash)}</p>
              <p className="text-xs text-gray-400 mt-1">current {inrL(c.current_accounts)} − overdraft {inrL(-c.cash_credit_drawn)} · as of {data.balance_as_of || '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Min cash you should hold</p>
              <p className="text-2xl font-bold font-mono mt-1">{inr(r.min_cash_required)}</p>
              <p className="text-xs text-gray-400 mt-1">always, on {data.date}</p>
            </CardContent></Card>
            <Card className={short ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}><CardContent className="p-5">
              <p className="text-xs uppercase tracking-wide flex items-center gap-1">{short ? <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> : <ShieldCheck className="w-3.5 h-3.5 text-green-700" />} {short ? 'Shortfall' : 'Surplus'}</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${short ? 'text-red-700' : 'text-green-700'}`}>{inr(Math.abs(r.surplus_or_shortfall))}</p>
              <p className="text-xs text-gray-500 mt-1">{short ? 'below the minimum — raise cash / collect AR' : 'above the minimum — healthy'}</p>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <button aria-label="Previous month" className="p-1.5 rounded-md hover:bg-gray-100" onClick={() => setViewDate(new Date(y, m - 1, 1))}><ChevronLeft className="w-4 h-4" /></button>
                <CardTitle className="text-sm">{RES_MONTHS[m]} {y}</CardTitle>
                <button aria-label="Next month" className="p-1.5 rounded-md hover:bg-gray-100" onClick={() => setViewDate(new Date(y, m + 1, 1))}><ChevronRight className="w-4 h-4" /></button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-gray-400 text-center">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((x, i) => <div key={i}>{x}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">{cells}</div>
                <p className="text-[11px] text-gray-400 mt-3 text-center">Pick a date to see the requirement for that day</p>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="text-base">How the minimum is built</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <ResRow label="Operating buffer" sub={`Daily burn ${inr(b.daily_burn)} × ${cy.net_occ_days}-day cash cycle (DSO ${cy.dso_days}d)`} value={inr(r.operating_buffer)} />
                <ResRow label={`Safety reserve (${r.safety_months} mo)`} sub={`${r.safety_months} × monthly fixed cost ${inrL(b.monthly_fixed_cost)}`} value={inr(r.safety_reserve)} />
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="font-semibold">Minimum cash required</span>
                  <span className="font-mono font-bold text-lg">{inr(r.min_cash_required)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <ResMini label="Monthly payroll" value={inrL(b.monthly_payroll)} />
                  <ResMini label="Monthly overheads" value={inrL(b.monthly_recurring)} />
                  <ResMini label="Fixed cost / mo" value={inrL(b.monthly_fixed_cost)} />
                  <ResMini label="Daily burn" value={inr(b.daily_burn)} />
                </div>
                {b.basis === 'bank_actual' && (
                  <p className="text-xs text-gray-500">No DESENO fixed costs are tagged, so burn uses the average monthly outflow from DESENO&apos;s bank statements.</p>
                )}
                {b.basis === 'none' && <p className="text-xs text-gray-500">No DESENO costs recorded yet.</p>}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {h.runway_days == null ? (
              <ResGauge icon={Activity} label="Runway" value="—" sub="no fixed costs recorded" />
            ) : c.net_cash <= 0 ? (
              <ResGauge icon={Activity} label="Runway" value="0d" sub="no positive cash" bad />
            ) : (
              <ResGauge icon={Activity} label="Runway" value={`${h.runway_days}d`} sub={`${h.runway_months} months at current burn`} bad={h.runway_days < 30} />
            )}
            <ResGauge icon={Gauge} label="Current ratio" value={h.current_ratio ?? '—'} sub={`target ${h.current_ratio_target}`} bad={h.current_ratio != null && h.current_ratio < 1.33} />
            <ResGauge icon={Gauge} label="Quick ratio" value={h.quick_ratio ?? '—'} sub={`target ${h.quick_ratio_target}`} bad={h.quick_ratio != null && h.quick_ratio < 1} />
            <ResGauge icon={Banknote} label="Break-even rev" value={inrL(h.break_even_monthly_revenue)} sub="per month (SLC S2)" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResObligations title={`Money going out — next ${nt.horizon_days} days`} items={nt.out_items} total={nt.outflows} tone="red" />
            <ResObligations title={`Money coming in — next ${nt.horizon_days} days`} items={nt.in_items} total={nt.inflows} tone="green" />
          </div>

          <Card>
            <CardHeader className="cursor-pointer py-3" onClick={() => setShowFormulas(!showFormulas)}>
              <CardTitle className="text-sm flex items-center gap-2"><Info className="w-4 h-4" /> The formulas (SLC Basic Finance) <span className="text-gray-400 ml-auto">{showFormulas ? '▲' : '▼'}</span></CardTitle>
            </CardHeader>
            {showFormulas && (
              <CardContent className="space-y-1.5 text-xs text-gray-600 font-mono">
                {Object.values(data.formulas).map((v, i) => <div key={i}>• {v}</div>)}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function ResRow({ label, sub, value }) {
  return (
    <div className="flex justify-between items-start gap-3">
      <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-gray-400">{sub}</p></div>
      <span className="font-mono text-sm whitespace-nowrap">{value}</span>
    </div>
  );
}
function ResMini({ label, value }) {
  return <div className="bg-gray-50 rounded-md p-2"><p className="text-[11px] text-gray-500">{label}</p><p className="font-mono font-semibold">{value}</p></div>;
}
function ResGauge({ icon: Icon, label, value, sub, bad }) {
  return (
    <Card className={bad ? 'border-red-200' : ''}>
      <CardContent className="p-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</p>
        <p className={`text-xl font-bold font-mono mt-1 ${bad ? 'text-red-700' : ''}`}>{value}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}
function ResObligations({ title, items, total, tone }) {
  const color = tone === 'red' ? 'text-red-700' : 'text-green-700';
  const bg = tone === 'red' ? 'bg-red-50' : 'bg-green-50';
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex justify-between"><span>{title}</span><span className={`font-mono ${color}`}>{inr(total)}</span></CardTitle></CardHeader>
      <CardContent>
        {(!items || items.length === 0) ? <p className="text-sm text-gray-400 py-3 text-center">Nothing scheduled in this window</p> : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {items.map((it, i) => (
              <div key={i} className={`flex justify-between items-center p-2 rounded ${bg}`}>
                <div className="min-w-0"><p className="text-sm truncate" title={it.label}>{it.label}</p>{it.date && <p className="text-[11px] text-gray-500">{it.date}</p>}</div>
                <span className={`font-mono text-sm ${color} whitespace-nowrap`}>{inr(it.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
